<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.stock.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="md" type="warning" @click="openAdjust">
            <i class="fa fa-balance-scale"></i> {{ $t("modern_trade.stock.adjust") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="companyFilter"
                  :placeholder="$t('modern_trade.company.name')"
                  clearable
                  @change="loadData"
                  class="w-100"
                >
                  <el-option
                    v-for="c in companies"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input
                v-model="productSearch"
                prepend-icon="fas fa-search"
                :placeholder="$t('search')"
                @keyup.enter.native="loadData"
              ></base-input>
            </div>
            <div class="col-md-3">
              <div class="d-flex align-items-center">
                <label class="mr-2 mb-0 text-sm">{{ $t("modern_trade.stock.group_by_lot") }}</label>
                <el-switch
                  v-model="groupByLot"
                  @change="loadData"
                  active-color="#11cdef"
                  inactive-color="#adb5bd"
                ></el-switch>
                <label class="ml-2 mb-0 text-sm">{{ groupByLot ? $t("modern_trade.stock.group_by_lot") : $t("modern_trade.stock.group_by_product") }}</label>
              </div>
            </div>
            <div class="col-md-2">
              <div class="d-flex align-items-center">
                <el-switch
                  v-model="hideZero"
                  @change="loadData"
                  active-color="#2dce89"
                ></el-switch>
                <label class="ml-2 mb-0 text-sm">{{ $t("modern_trade.stock.hide_zero") }}</label>
              </div>
            </div>
            <div class="col-md-1 text-right">
              <base-button size="sm" type="primary" @click="loadData">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table
            :data="rows"
            :row-key="rowKey"
            header-row-class-name="thead-light"
            @expand-change="onExpandChange"
          >
            <!-- Movement ledger only makes sense per-lot; product mode has no
                 lot_no so hide the expand column entirely to avoid firing the
                 movements query with undefined params. -->
            <el-table-column v-if="groupByLot" type="expand">
              <template slot-scope="{ row }">
                <div class="px-4 py-2">
                  <strong class="text-muted small">{{ $t("modern_trade.stock.movements") }}</strong>
                  <div v-if="row._loadingMoves" class="text-muted small">Loading...</div>
                  <table v-else-if="row._moves && row._moves.length" class="table table-sm table-bordered mt-1">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Note</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="m in row._moves" :key="m.id">
                        <td>{{ formatDate(m.created_at) }}</td>
                        <td>{{ movementTypeLabel(m.movement_type) }}</td>
                        <td :class="m.qty > 0 ? 'text-success' : 'text-danger'">{{ m.qty > 0 ? '+' : '' }}{{ m.qty }}</td>
                        <td>{{ m.note || "-" }}</td>
                        <td>{{ m.created_by_name || "-" }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-else class="text-muted small mb-0">{{ $t("modern_trade.stock.no_movements") }}</p>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.company.name')" min-width="150">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column label="Product" min-width="180">
              <template slot-scope="{ row }">
                {{ row.product_name || row.product_id }}
                <small v-if="row.barcode" class="d-block text-muted">{{ row.barcode }}</small>
              </template>
            </el-table-column>
            <el-table-column v-if="groupByLot" :label="$t('modern_trade.stock.lot_no')" prop="lot_no" min-width="120"></el-table-column>
            <el-table-column v-if="groupByLot" :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
            <el-table-column v-if="groupByLot" :label="$t('modern_trade.stock.days_to_expire')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span :class="daysClass(row.days_to_expire)">
                  {{ row.days_to_expire !== undefined ? row.days_to_expire : "-" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.stock.qty')" min-width="80" align="right">
              <template slot-scope="{ row }">
                <span :class="rowQty(row) <= 0 ? 'text-muted' : ''">{{ rowQty(row) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Adjust Dialog -->
    <el-dialog
      :title="$t('modern_trade.stock.adjust')"
      :visible.sync="showAdjustDialog"
      width="480px"
    >
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.name") }} *</label>
            <el-select v-model="adjustForm.company_id" class="w-100" filterable>
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">Product ID *</label>
            <input v-model.number="adjustForm.product_id" class="form-control" type="number" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.stock.lot_no") }} *</label>
            <input v-model="adjustForm.lot_no" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.stock.adjust_qty") }} * (บวก/ลบ)</label>
            <input v-model.number="adjustForm.qty" class="form-control" type="number" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.stock.adjust_reason") }} *</label>
            <input v-model="adjustForm.reason" class="form-control" type="text" />
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showAdjustDialog = false">{{ $t("close") }}</el-button>
        <el-button type="warning" :loading="adjusting" @click="handleAdjust">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Switch, Dialog, Button } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";
import Vue from "vue";

export default {
  name: "MtStock",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
  },
  data() {
    return {
      loading: false,
      adjusting: false,
      rows: [],
      companies: [],
      total: 0,
      page: 1,
      perPage: 30,
      companyFilter: null,
      productSearch: "",
      groupByLot: true,
      hideZero: true,
      showAdjustDialog: false,
      adjustForm: {
        company_id: null,
        product_id: null,
        lot_no: "",
        qty: 0,
        reason: "",
      },
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
          group_by: this.groupByLot ? "lot" : "product",
        };
        if (this.companyFilter) params.company_id = this.companyFilter;
        if (this.productSearch) params.search = this.productSearch;
        if (this.hideZero) params.nonzero = 1;
        const res = await ModernTradeService.stockList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        // Add reactive properties for movement expansion
        this.rows = (d.items || []).map(r => Object.assign({ _moves: null, _loadingMoves: false }, r));
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    rowKey(row) {
      // Lot mode has a real per-lot id; product mode only has product_id, so
      // derive a stable unique key to avoid duplicate row-key warnings.
      return this.groupByLot ? row.id : "p" + row.product_id;
    },
    rowQty(row) {
      // Lot mode returns `qty`; product-group mode returns `total_qty`.
      return row.qty != null ? row.qty : row.total_qty;
    },
    onExpandChange(row, expandedRows) {
      if (expandedRows.includes(row)) this.loadMovements(row);
    },
    async loadMovements(row) {
      if (row._moves !== null) return; // already loaded
      Vue.set(row, "_loadingMoves", true);
      try {
        const res = await ModernTradeService.stockMovements({
          company_id: row.company_id,
          product_id: row.product_id,
          lot_no: row.lot_no,
          per_page: 20,
        });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        Vue.set(row, "_moves", d.items || []);
      } catch (e) {
        Vue.set(row, "_moves", []);
      } finally {
        Vue.set(row, "_loadingMoves", false);
      }
    },
    openAdjust() {
      this.adjustForm = { company_id: null, product_id: null, lot_no: "", qty: 0, reason: "" };
      this.showAdjustDialog = true;
    },
    async handleAdjust() {
      if (!this.adjustForm.company_id || !this.adjustForm.product_id || !this.adjustForm.lot_no) {
        swal.fire({ title: this.$t("modern_trade.stock.fill_all_required"), icon: "warning" });
        return;
      }
      if (!this.adjustForm.qty || this.adjustForm.qty === 0) {
        swal.fire({ title: this.$t("modern_trade.stock.qty_not_zero"), icon: "warning" });
        return;
      }
      if (!this.adjustForm.reason) {
        swal.fire({ title: this.$t("modern_trade.stock.reason_required"), icon: "warning" });
        return;
      }
      this.adjusting = true;
      try {
        await ModernTradeService.stockAdjust(this.adjustForm);
        this.showAdjustDialog = false;
        this.loadData();
        swal.fire({ title: this.$t("modern_trade.stock.adjust_success"), icon: "success", timer: 1500, showConfirmButton: false });
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.adjusting = false;
      }
    },
    daysClass(days) {
      if (days === undefined || days === null) return "";
      if (days < 0) return "text-danger font-weight-bold";
      if (days <= 30) return "text-warning font-weight-bold";
      if (days <= 60) return "text-orange";
      return "";
    },
    movementTypeLabel(t) {
      switch (t) {
        case 1: return this.$t("modern_trade.stock.movement_type_receive");
        case 2: return this.$t("modern_trade.stock.movement_type_sale");
        case 3: return this.$t("modern_trade.stock.movement_type_adjust");
        // MtMovementRecall — near-expiry return (spec 2026-11-03 §5), qty stored negative
        case 4: return this.$t("modern_trade.stock.movement_type_recall");
        default: return "-";
      }
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    },
  },
};
</script>
